import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { getFileExtension } from 'src/app/helpers/util';
import { LambdaService } from 'src/app/lambda.service';
import { environment } from 'src/environments/environment';
import {
  BackgroundConfig,
  ShopCompany,
  ShopCompanyTheme,
  ShopCompanyThemeFormChanges,
} from '../../shop-companies/shop-company.models';

type ThemeAssetField =
  | 'shopLogoLarge'
  | 'shopLogoWhite'
  | 'shopLogoSmall'
  | 'companyBanner'
  | 'shopFavicon';

@Component({
  selector: 'app-shop-company-theme-settings',
  templateUrl: './shop-company-theme-settings.component.html',
  styleUrls: ['./shop-company-theme-settings.component.scss'],
})
export class ShopCompanyThemeSettingsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() shopId!: number;
  @Input() shopCompanyId!: number;
  @Input() shopCompany: ShopCompany | null = null;
  @Input() parentSaving = false;
  @Output() saveThemeChanges = new EventEmitter<ShopCompanyThemeFormChanges>();

  themeForm: FormGroup;
  fonts: any[] = [];
  loading = false;
  saving = false;
  successMessage = '';
  errorMessage = '';

  headerBackground: BackgroundConfig | null = null;
  footerBackground: BackgroundConfig | null = null;
  useParentTheme = false;
  selectedFontFamily: string | null = null;

  assetFiles: Partial<Record<ThemeAssetField, File>> = {};
  assetPreviews: Partial<Record<ThemeAssetField, string | SafeUrl | null>> = {};

  private dirtyFields = new Set<keyof ShopCompanyTheme>();
  private objectUrlPreviews: Partial<Record<ThemeAssetField, string>> = {};
  private hasPendingChanges = false;
  private initializing = false;

  readonly colorFields: Array<{
    key: keyof ShopCompanyTheme;
    label: string;
    referenceImage: string;
  }> = [
    {
      key: 'shopLogoMainColor',
      label: 'MAIN',
      referenceImage: 'https://mobydyg-files.s3.amazonaws.com/mainColorExample',
    },
    {
      key: 'shopLogoMainColorLight',
      label: 'PRODUCT',
      referenceImage:
        'https://mobydyg-files.s3.us-east-1.amazonaws.com/tarjeta-producto.png',
    },
    {
      key: 'shopLogoMainColorDark',
      label: 'MENU',
      referenceImage:
        'https://mobydyg-files.s3.amazonaws.com/shopSecondaryColorExample',
    },
  ];

  readonly backgroundFields: Array<{
    key: 'headerBackground' | 'footerBackground';
    label: string;
  }> = [
    { key: 'headerBackground', label: 'HEADER' },
    { key: 'footerBackground', label: 'FOOTER' },
  ];

  readonly assetFields: Array<{
    key: ThemeAssetField;
    label: string;
    fileName: string;
    display: 'logo' | 'banner';
  }> = [
    {
      key: 'shopLogoLarge',
      label: 'DESKTOP_LOGO',
      fileName: 'main',
      display: 'logo',
    },
    {
      key: 'shopLogoWhite',
      label: 'FOOTER_WHITE_LOGO',
      fileName: 'secondary',
      display: 'logo',
    },
    {
      key: 'shopLogoSmall',
      label: 'MOBILE_LOGO',
      fileName: 'mobile',
      display: 'logo',
    },
    {
      key: 'companyBanner',
      label: 'COMPANY_BANNER',
      fileName: 'banner',
      display: 'banner',
    },
    {
      key: 'shopFavicon',
      label: 'FAVICON',
      fileName: 'favicon',
      display: 'logo',
    },
  ];

  readonly bannerAssetField = this.assetFields.find(
    (asset) => asset.key === 'companyBanner'
  )!;

  readonly headerFields: Array<{
    key: 'showSearchBar' | 'showAuthButtons' | 'showRegisterButtons' | 'showCart';
    label: string;
  }> = [
    { key: 'showSearchBar', label: 'SHOW_SEARCH_BAR' },
    { key: 'showAuthButtons', label: 'SHOW_AUTH_BUTTONS' },
    { key: 'showRegisterButtons', label: 'SHOW_REGISTER_BUTTONS' },
    { key: 'showCart', label: 'SHOW_CART' },
  ];

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private sanitizer: DomSanitizer
  ) {
    this.themeForm = this.fb.group({
      shopLogoLarge: [null],
      shopLogoWhite: [null],
      shopLogoSmall: [null],
      companyBanner: [null],
      shopFavicon: [null],
      shopLogoMainColor: [null],
      shopLogoMainColorLight: [null],
      shopLogoMainColorDark: [null],
      shopLogoMainColorTitleActive: [null],
      shopLogoMainColorTitlePlaceholder: [null],
      shopFontFamily1: [null],
      shopFontFamily2: [null],
      showSearchBar: [true],
      showAuthButtons: [true],
      showRegisterButtons: [true],
      showCart: [true],
      languageSelectorPosition: ['navbar'],
    });

    Object.keys(this.themeForm.controls).forEach((field) => {
      this.themeForm.get(field)?.valueChanges.subscribe(() => {
        if (!this.initializing) {
          this.dirtyFields.add(field as keyof ShopCompanyTheme);
          this.hasPendingChanges = true;
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadFonts();
    this.syncThemeFromCompany();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shopCompany']?.currentValue) {
      this.syncThemeFromCompany();
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrlPreviews();
  }

  private syncThemeFromCompany(): void {
    if (!this.shopCompany) {
      return;
    }
    this.initializing = true;
    this.errorMessage = '';
    this.themeForm.reset(
      {
        shopLogoLarge: this.shopCompany.shopLogoLarge ?? null,
        shopLogoWhite: this.shopCompany.shopLogoWhite ?? null,
        shopLogoSmall: this.shopCompany.shopLogoSmall ?? null,
        companyBanner: this.shopCompany.companyBanner ?? null,
        shopFavicon: this.shopCompany.shopFavicon ?? null,
        shopLogoMainColor: this.shopCompany.shopLogoMainColor ?? null,
        shopLogoMainColorLight: this.shopCompany.shopLogoMainColorLight ?? null,
        shopLogoMainColorDark: this.shopCompany.shopLogoMainColorDark ?? null,
        shopLogoMainColorTitleActive:
          this.shopCompany.shopLogoMainColorTitleActive ?? null,
        shopLogoMainColorTitlePlaceholder:
          this.shopCompany.shopLogoMainColorTitlePlaceholder ?? null,
        shopFontFamily1:
          this.shopCompany.shopFontFamily1 ??
          this.shopCompany.shopFontFamily2 ??
          null,
        shopFontFamily2:
          this.shopCompany.shopFontFamily2 ??
          this.shopCompany.shopFontFamily1 ??
          null,
        showSearchBar: this.shopCompany.showSearchBar ?? true,
        showAuthButtons: this.shopCompany.showAuthButtons ?? true,
        showRegisterButtons: this.shopCompany.showRegisterButtons ?? true,
        showCart: this.shopCompany.showCart ?? true,
        languageSelectorPosition:
          this.shopCompany.languageSelectorPosition ?? 'navbar',
      },
      { emitEvent: false }
    );
    this.headerBackground = this.parseBackground(this.shopCompany.headerBackground);
    this.footerBackground = this.parseBackground(this.shopCompany.footerBackground);
    this.selectedFontFamily =
      this.shopCompany.shopFontFamily1 ??
      this.shopCompany.shopFontFamily2 ??
      null;
    this.useParentTheme = this.shopCompany.useParentTheme === true;
    this.assetPreviews = {
      shopLogoLarge: this.getAssetPreviewValue(
        'shopLogoLarge',
        this.shopCompany.shopLogoLarge
      ),
      shopLogoWhite: this.getAssetPreviewValue(
        'shopLogoWhite',
        this.shopCompany.shopLogoWhite
      ),
      shopLogoSmall: this.getAssetPreviewValue(
        'shopLogoSmall',
        this.shopCompany.shopLogoSmall
      ),
      companyBanner: this.getAssetPreviewValue(
        'companyBanner',
        this.shopCompany.companyBanner
      ),
      shopFavicon: this.getAssetPreviewValue(
        'shopFavicon',
        this.shopCompany.shopFavicon
      ),
    };
    if (!this.hasPendingAssetUploads()) {
      this.dirtyFields.clear();
      this.hasPendingChanges = false;
    }
    this.initializing = false;
    this.updateDisabledState();
  }

  async loadFonts(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getFonts();
      this.fonts = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
    } catch (error) {
      console.error('Error loading fonts:', error);
    }
  }

  selectAsset(field: ThemeAssetField, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.revokeObjectUrlPreview(field);
    const previewUrl = URL.createObjectURL(file);
    this.assetFiles[field] = file;
    this.objectUrlPreviews[field] = previewUrl;
    this.assetPreviews = {
      ...this.assetPreviews,
      [field]: this.sanitizer.bypassSecurityTrustUrl(previewUrl),
    };
    console.log('[SHOP COMPANY IMAGE PREVIEW]', field, previewUrl);
    this.dirtyFields.add(field);
    this.hasPendingChanges = true;
  }

  toggleParentTheme(): void {
    this.useParentTheme = !this.useParentTheme;
    this.hasPendingChanges = true;
    this.updateDisabledState();
  }

  onFontChange(fontFamily: string | null): void {
    this.selectedFontFamily = fontFamily || null;
    this.themeForm.patchValue(
      {
        shopFontFamily1: this.selectedFontFamily,
        shopFontFamily2: this.selectedFontFamily,
      },
      { emitEvent: true }
    );
  }

  getSelectedFontName(): string {
    const selected = this.fonts.find(
      (font) => font.fontFamily === this.selectedFontFamily
    );
    return selected?.fontName || this.selectedFontFamily || '';
  }

  setBackgroundMode(
    field: 'headerBackground' | 'footerBackground',
    mode: 'solid' | 'gradient'
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current = this.getBackground(field);
    const baseColor = current?.solidColor || '#EA803F';
    const next: BackgroundConfig =
      mode === 'solid'
        ? { mode: 'solid', solidColor: baseColor }
        : {
            mode: 'gradient',
            solidColor: baseColor,
            gradient: {
              angle: current?.gradient?.angle ?? 135,
              stops: current?.gradient?.stops?.length
                ? current.gradient.stops
                : [
                    { color: baseColor, position: 0 },
                    { color: '#FFFFFF', position: 100 },
                  ],
            },
          };
    this.setBackground(field, next);
  }

  updateBackgroundColor(
    field: 'headerBackground' | 'footerBackground',
    color: string,
    stopIndex?: number
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current =
      this.getBackground(field) || ({ mode: 'solid' } as BackgroundConfig);
    if (current.mode === 'gradient' && current.gradient && stopIndex !== undefined) {
      current.gradient.stops[stopIndex].color = color;
    } else {
      current.solidColor = color;
    }
    this.setBackground(field, { ...current });
  }

  updateGradientAngle(
    field: 'headerBackground' | 'footerBackground',
    angle: number
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current = this.getBackground(field);
    if (current?.gradient) {
      current.gradient.angle = Math.max(0, Math.min(360, Number(angle) || 0));
      this.setBackground(field, { ...current });
    }
  }

  updateGradientPosition(
    field: 'headerBackground' | 'footerBackground',
    index: number,
    position: number
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current = this.getBackground(field);
    if (current?.gradient?.stops[index]) {
      current.gradient.stops[index].position = Math.max(
        0,
        Math.min(100, Number(position) || 0)
      );
      this.setBackground(field, { ...current });
    }
  }

  addGradientStop(
    field: 'headerBackground' | 'footerBackground'
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current = this.getBackground(field);
    if (!current?.gradient || current.gradient.stops.length >= 5) {
      return;
    }
    const stops = [...current.gradient.stops].sort(
      (a, b) => a.position - b.position
    );
    let position = 50;
    let largestGap = -1;
    for (let index = 0; index < stops.length - 1; index++) {
      const gap = stops[index + 1].position - stops[index].position;
      if (gap > largestGap) {
        largestGap = gap;
        position = Math.round(stops[index].position + gap / 2);
      }
    }
    current.gradient.stops.push({
      color: stops[0]?.color || '#EA803F',
      position,
    });
    this.setBackground(field, { ...current });
  }

  removeGradientStop(
    field: 'headerBackground' | 'footerBackground',
    index: number
  ): void {
    if (this.useParentTheme) {
      return;
    }
    const current = this.getBackground(field);
    if (!current?.gradient || current.gradient.stops.length <= 2) {
      return;
    }
    current.gradient.stops.splice(index, 1);
    this.setBackground(field, { ...current });
  }

  buildBackgroundStyle(config: BackgroundConfig | null): string {
    if (!config) {
      return 'transparent';
    }
    if (config.mode === 'solid') {
      return config.solidColor || 'transparent';
    }
    const gradient = config.gradient;
    if (!gradient || gradient.stops.length < 2) {
      return config.solidColor || 'transparent';
    }
    const stops = [...gradient.stops]
      .sort((a, b) => a.position - b.position)
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(', ');
    return `linear-gradient(${gradient.angle}deg, ${stops})`;
  }

  async saveTheme(): Promise<void> {
    if (
      !this.hasPendingChanges &&
      this.dirtyFields.size === 0 &&
      !this.hasPendingAssetUploads()
    ) {
      this.successMessage = 'NO_THEME_CHANGES';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    try {
      this.saveThemeChanges.emit(await this.buildChanges());
    } catch (error) {
      console.error('Error preparing shop company theme:', error);
      this.errorMessage = 'SHOP_COMPANY_THEME_SAVE_ERROR';
    } finally {
      this.saving = false;
    }
  }

  async buildChanges(): Promise<ShopCompanyThemeFormChanges> {
    const rawValue = this.themeForm.getRawValue();
    const changes: ShopCompanyThemeFormChanges = {
      useParentTheme: this.useParentTheme,
      shopFavicon: this.normalizeValue(rawValue.shopFavicon),
      shopLogoWhite: this.normalizeValue(rawValue.shopLogoWhite),
      shopLogoLarge: this.normalizeValue(rawValue.shopLogoLarge),
      shopLogoSmall: this.normalizeValue(rawValue.shopLogoSmall),
      companyBanner: this.normalizeValue(rawValue.companyBanner),
      shopLogoMainColor: this.normalizeValue(rawValue.shopLogoMainColor),
      shopLogoMainColorLight: this.normalizeValue(rawValue.shopLogoMainColorLight),
      shopLogoMainColorDark: this.normalizeValue(rawValue.shopLogoMainColorDark),
      shopLogoMainColorTitleActive: this.normalizeValue(
        rawValue.shopLogoMainColorTitleActive
      ),
      shopLogoMainColorTitlePlaceholder: this.normalizeValue(
        rawValue.shopLogoMainColorTitlePlaceholder
      ),
      headerBackground: this.headerBackground,
      footerBackground: this.footerBackground,
      shopFontFamily1: this.selectedFontFamily,
      shopFontFamily2: this.selectedFontFamily,
      showSearchBar: rawValue.showSearchBar ?? true,
      showAuthButtons: rawValue.showAuthButtons ?? true,
      showRegisterButtons: rawValue.showRegisterButtons ?? true,
      showCart: rawValue.showCart ?? true,
      languageSelectorPosition: rawValue.languageSelectorPosition ?? 'navbar',
    };

    for (const asset of this.assetFields) {
      const file = this.assetFiles[asset.key];
      if (!file) {
        continue;
      }
      const extension = getFileExtension(file);
      const uniqueFileName = `${asset.fileName}-${Date.now()}.${extension}`;
      const key =
        `${this.shopId}/${environment.stage}/images/shop-companies/` +
        `${this.shopCompanyId}/logos/${uniqueFileName}`;
      const uploaded: any = await this.lambdaService.uploadFile(key, file, {});
      changes[asset.key] = uploaded.imageURL;
      this.assetPreviews = {
        ...this.assetPreviews,
        [asset.key]: uploaded.imageURL,
      };
      this.themeForm.get(asset.key)?.setValue(uploaded.imageURL, {
        emitEvent: false,
      });
      this.revokeObjectUrlPreview(asset.key);
      delete this.assetFiles[asset.key];
    }

    this.dirtyFields.clear();
    this.hasPendingChanges = false;
    return changes;
  }

  getBackground(
    field: 'headerBackground' | 'footerBackground'
  ): BackgroundConfig | null {
    return field === 'headerBackground'
      ? this.headerBackground
      : this.footerBackground;
  }

  private setBackground(
    field: 'headerBackground' | 'footerBackground',
    value: BackgroundConfig
  ): void {
    if (field === 'headerBackground') {
      this.headerBackground = value;
    } else {
      this.footerBackground = value;
    }
    this.dirtyFields.add(field);
    this.hasPendingChanges = true;
  }

  private parseBackground(value: any): BackgroundConfig | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value) as BackgroundConfig;
      } catch {
        return { mode: 'solid', solidColor: value };
      }
    }
    return value as BackgroundConfig;
  }
  private updateDisabledState(): void {
    if (this.useParentTheme) {
      this.themeForm.disable({ emitEvent: false });
    } else {
      this.themeForm.enable({ emitEvent: false });
    }
  }

  private normalizeValue(value: any): any {
    return value === '' || value === undefined ? null : value;
  }

  private revokeObjectUrlPreview(field: ThemeAssetField): void {
    const previewUrl = this.objectUrlPreviews[field];
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      delete this.objectUrlPreviews[field];
    }
  }

  private revokeObjectUrlPreviews(): void {
    this.assetFields.forEach((asset) => this.revokeObjectUrlPreview(asset.key));
  }

  private hasPendingAssetUploads(): boolean {
    return this.assetFields.some((asset) => !!this.assetFiles[asset.key]);
  }

  private getAssetPreviewValue(
    field: ThemeAssetField,
    fallbackUrl?: string | null
  ): string | SafeUrl | null {
    const objectUrl = this.objectUrlPreviews[field];
    return objectUrl
      ? this.sanitizer.bypassSecurityTrustUrl(objectUrl)
      : fallbackUrl || null;
  }
}
